/**
 * Materiais por especialista de IA — Fase 4 do roadmap de IA (`db/migrations/V0021`).
 *
 * Fonte: `gestao_administrativa.py` (rotas `/materiais*`) + `V0021__materiais_especialista.sql`,
 * lidos diretamente no backend — não há handoff/shot para esta tela ainda.
 *
 * 🔒 Catálogo GLOBAL (sem `idCliente`): é configuração de produto (qual material o Runtime
 * injeta no prompt de cada especialista), não dado de cliente — mesmo molde dos catálogos
 * de `administracao.ts` (`clienteSchema` etc.), nunca `chaveDeQuery`.
 *
 * 🔒 `formato` decide a forma: `texto` sempre tem `conteudo` e nunca `tamanhoBytes`; `pdf`/
 * `docx` é o oposto — o banco tem um CHECK que impõe exatamente essa coerência
 * (`materiais_forma_coerente`), e o backend nunca devolve o meio-termo.
 */
import { z } from 'zod'

import { id } from './comum'

/** ISO 8601 com hora — mesmo raciocínio de `compliance.ts`: formato não fixado, não validado. */
const instante = z.string().min(1)

export const especialistaIaSchema = z.enum(['finops', 'orcamento', 'compliance'])
export type EspecialistaIa = z.infer<typeof especialistaIaSchema>

export const formatoDoMaterialSchema = z.enum(['pdf', 'docx', 'texto'])
export type FormatoDoMaterial = z.infer<typeof formatoDoMaterialSchema>

/** `_material_publico()` tira `chaveS3` da resposta — quem precisa do arquivo pede uma
 * URL assinada (`download-url`), mesma disciplina de `Evidencia`/`Manual` em `compliance.ts`. */
export const materialSchema = z.object({
  id,
  especialista: especialistaIaSchema,
  titulo: z.string(),
  formato: formatoDoMaterialSchema,
  conteudo: z.string().nullable(),
  tamanhoBytes: z.number().int().nullable(),
  ordem: z.number().int(),
  ativo: z.boolean(),
  criadoEm: instante,
  /** FK pra `administracao.usuarios.id_usuario` — nullable porque a FK não é NOT NULL. */
  criadoPor: id.nullable(),
  atualizadoEm: instante,
})
export type Material = z.infer<typeof materialSchema>

/** A resposta de `upload-url` — mesmo formato de `UrlDeUpload` em `compliance.ts` (o backend
 * reusa o mesmo helper `armazenamento_s3.emitir_upload` para os dois recursos). */
export const urlDeUploadDeMaterialSchema = z.object({
  uploadUrl: z.string().min(1),
  chave: z.string().min(1),
  expiraEm: instante,
  cabecalhos: z.record(z.string(), z.string()),
})
export type UrlDeUploadDeMaterial = z.infer<typeof urlDeUploadDeMaterialSchema>

export const urlDeDownloadDeMaterialSchema = z.object({ url: z.string().min(1), expiraEm: instante })

/** O que a tela monta antes do upload — `arquivo`/`conteudo` nunca viajam juntos no POST
 * (o backend valida XOR via `materiais_forma_coerente`), mesmo padrão de `NovaEvidencia`. */
export type NovoMaterial =
  | { especialista: EspecialistaIa; titulo: string; formato: 'texto'; conteudo: string }
  | { especialista: EspecialistaIa; titulo: string; formato: 'pdf' | 'docx'; arquivo: File }

/** `PATCH /materiais/{id}` — `especialista`/`formato`/`chave` nunca mudam depois de criado
 * (um material não muda de dono nem de forma); só estes 4 campos são editáveis. */
export type PatchDeMaterial = Partial<{
  titulo: string
  ordem: number
  ativo: boolean
  conteudo: string
}>
